import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { SyntheticEvent } from "react";
import { useNavigate } from "react-router";

import { useQueryParam } from "@/hooks/useQueryParam";
import { clearContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useLogout as useApiLogout } from "@/services/auth";
import {
  AlertTypes,
  clearAuthAtom,
  showsSessionExpiredModalAtom,
  verifierAtom,
} from "@/utils/atoms";
import { isPartnerHashRoute } from "@/utils/partnerRoute";

import { useAlert } from "./useAlert";

export const useLogout = () => {
  const clearAuth = useSetAtom(clearAuthAtom);
  const setShowSessionExpiredModal = useSetAtom(showsSessionExpiredModalAtom);
  const showAlert = useAlert();

  const setVerifier = useSetAtom(verifierAtom);
  const [, setCode] = useQueryParam("code");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const clearAppContext = () => {
    const isPartnerRoute = isPartnerHashRoute();
    clearContext();
    setCode("");
    queryClient.clear();
    clearAuth();
    setVerifier(undefined);
    setShowSessionExpiredModal(undefined);
    localStorage.clear();
    // Remove info from sessionStorage to show attention bubble again after login
    sessionStorage.removeItem("showFriendsWithBenefitsHint");
    navigate(
      isPartnerRoute ? portalRoutes.welcomePartner : portalRoutes.welcome,
    );
  };

  const { mutate } = useApiLogout({
    mutation: {
      onSuccess: () => {
        clearAppContext();
      },
      onError: () => {
        clearAppContext();
      },
    },
  });

  // As this is commonly wired straight into button onClicks, React might call it with the SyntheticEvent...
  const logout = (emailVerificationErrorOrEvent?: string | SyntheticEvent) => {
    // ...so we need to avoid an event triggering an error alert
    const emailVerificationError =
      typeof emailVerificationErrorOrEvent === "string"
        ? emailVerificationErrorOrEvent
        : undefined;

    if (emailVerificationError) {
      showAlert({
        type: AlertTypes.error,
        text: emailVerificationError,
      });
    }

    mutate();
  };

  return logout;
};
